// ColumnDeck.js — close/reopen + lazy-load missing columns + live drag-reorder
// -----------------------------------------------------------------------------
// Client changes implemented:
// - NO add-column / swap-column features
// - Any column can be closed; ALL columns may be closed (nothing auto-reopens)
// - Header/nav buttons can toggle/open a specific column (by id OR by data-section)
// - If a target column isn't present on the page, we fetch it from a URL (e.g. /about)
// - Drag handles reorder columns with a live placeholder gap; order commits on drop
// - Prevent scrollbars from appearing while dragging
// - Hide/disable drag handles when <= 1 visible column
// - Hide the header button for a column while that column is open/active
//
// Expected column markup (your Twig is already basically this):
// <section class="col" data-section="editorial" data-id="editorial">
//   <header class="head">
//     <button class="drag-handle" type="button">⋮⋮</button>
//     <h3>Editorial</h3>
//     <button class="close" type="button">×</button>
//   </header>
//   <div class="scroll">...</div>
// </section>
//
// Header/nav buttons:
// <button data-toggle-column="editorial" data-url="/editorial">Editorial</button>
// <button data-open-column="about" data-url="/about">About</button>
//
// About “tab” behaviour:
// <div class="tab" data-url="/about" data-id="about">About</div>
// - if data-id === "about", we treat it like a toggle-column and lazy-load into the deck.

export default class ColumnDeck {
  constructor(opts = {}) {
    this.deck = opts.mount instanceof Element ? opts.mount : document.querySelector(opts.mount);
    this.tabs = opts.tabsMount instanceof Element
      ? opts.tabsMount
      : (opts.tabsMount ? document.querySelector(opts.tabsMount) : null);

    if (!this.deck) throw new Error('ColumnDeck: opts.mount is required');

    this.mobileBP = opts.mobileBreakpoint ?? 900;
    this._skipNavSync = !!opts.skipInitialNavSync;
    this.navMount = opts.navMount || document;

    this.openAttr = opts.openAttr || 'data-open-column';
    this.toggleAttr = opts.toggleAttr || 'data-toggle-column';

    // When fetching a page, which node’s children become the column content:
    this.contentSelector = opts.contentSelector || '[data-section-content], .scroll, main, #main, .content, article, .page';
    this.stripSelectors = opts.stripSelectors || ['.tabs', '.deck-nav', 'nav.deck-nav'];

    // State
    this.columns = new Map(); // id -> { root, head, scroll, handle, close, section }
    this.order = [];
    this._inflightColumns = new Map(); // key -> Promise

    // Drag state
    this._drag = {
      id: null,
      col: null,
      handle: null,
      pointerId: null,
      offsetX: 0,
      offsetY: 0,
      placeholder: null,
      lastInsertKey: null,
      raf: 0,
      angle: 0,
      angularVel: 0,
      prevClientX: 0,
      swingRaf: 0,
      locked: false,
      prev: {
        htmlOverflow: '',
        bodyOverflow: '',
        htmlOverscroll: '',
        bodyOverscroll: '',
        scrollOverflows: new Map(),
      },
    };

    // Binds
    this._onNavActivate = this._onNavActivate.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    // Init
    this._adoptInitialColumns();
    this._bindNav();
    this._bindTabs(); // About tab + keep other tab links working
    this._updateVisibleCount();
    this._syncReorderAvailability();
    this._syncNavButtons();

    window.addEventListener('resize', () => {
      this._updateVisibleCount();
      this._syncReorderAvailability();
      this._syncNavButtons();
    }, { passive: true });
  }

  /* =============================================================================
    Public API
  ============================================================================= */

  openColumn(key, { scroll = true, active = true } = {}) {
    const c = this._getColumnByKey(key);
    if (!c) return false;

    // Remove collapsed, add opening class for pop effect
    c.root.classList.remove('is-collapsed');
    c.root.classList.add('is-opening');

    // Remove opening class after a frame to trigger transition back to normal
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        c.root.classList.remove('is-opening');
      });
    });

    if (active) {
      this.columns.forEach(({ root }) => root.classList.remove('is-active'));
      c.root.classList.add('is-active');
    }

    if (scroll) {
      if (this._isMobile() && c.head) {
        c.head.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        c.root.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }

    this._updateVisibleCount();
    this._syncReorderAvailability();
    this._syncNavButtons();
    return true;
  }

  collapse(key, force = true) {
    const c = this._getColumnByKey(key);
    if (!c) return false;

    if (force) {
      // Phase 1: Pop and scale up animation
      c.root.classList.add('is-closing');
      c.root.classList.remove('is-active');

      // Phase 2: After pop completes, shrink width and let siblings grow
      setTimeout(() => {
        c.root.classList.add('is-shrinking');
        this._updateVisibleCount();
        this._syncReorderAvailability();
        this._syncNavButtons();
      }, 250); // Wait for pop animation

      // Phase 3: Fully hide after resize completes
      setTimeout(() => {
        c.root.classList.remove('is-closing');
        c.root.classList.remove('is-shrinking');
        c.root.classList.add('is-collapsed');
        this._updateVisibleCount();
        this._syncReorderAvailability();
        this._syncNavButtons();
      }, 800); // 250ms pop + 550ms resize with bounce

      return true;
    } else {
      c.root.classList.remove('is-collapsed');
    }

    this._updateVisibleCount();
    this._syncReorderAvailability();
    this._syncNavButtons();
    return true;
  }

  toggleColumn(key, { scroll = false, active = true } = {}) {
    const c = this._getColumnByKey(key);
    if (!c) return false;

    const wasCollapsed = c.root.classList.contains('is-collapsed');
    if (wasCollapsed) return this.openColumn(key, { scroll, active });
    return this.collapse(key, true);
  }

  /* =============================================================================
    Adoption (SSR columns)
  ============================================================================= */

  _adoptInitialColumns() {
    const els = Array.from(this.deck.querySelectorAll('.col'));
    els.forEach((colEl, i) => this._adoptColumnElement(colEl, i));
    this._recomputeOrderFromDOM();
  }

  _adoptColumnElement(colEl, index = 0) {
    if (!colEl || !colEl.classList?.contains('col')) return;

    const id = (colEl.dataset.id != null && colEl.dataset.id !== '')
      ? String(colEl.dataset.id)
      : (colEl.dataset.section ? String(colEl.dataset.section) : `col-${index}`);

    colEl.dataset.id = id;

    const head = colEl.querySelector('.head') || colEl.querySelector('header');
    const scroll = colEl.querySelector('.scroll') || colEl;

    const handle = head || null;
    const close = head?.querySelector('button.close, .close') || null;

    // Make the entire head bar draggable (not just the handle icon)
    if (handle) {
      handle.setAttribute('draggable', 'false');
      handle.addEventListener('dragstart', (e) => e.preventDefault());
      handle.addEventListener('mousedown', (e) => { if (!e.target.closest('.close')) e.preventDefault(); });
      handle.addEventListener('pointerdown', (e) => {
        // Don't start drag when clicking the close button
        if (e.target.closest('.close')) return;
        this._onPointerDown(e, id);
      }, { passive: false });
      handle.style.touchAction = 'none';
      handle.style.cursor = 'grab';
    }

    if (close) {
      close.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.collapse(id, true);
      });
    }

    // Mobile: tap header to toggle the column open/closed (only one open at a time)
    if (head) {
      head.addEventListener('click', (e) => {
        if (!this._isMobile()) return;
        if (e.target.closest('.close, .drag-handle')) return;
        const col = this.columns.get(id);
        const wasCollapsed = col?.root.classList.contains('is-collapsed');
        if (wasCollapsed) {
          // Snap-close every other column before opening this one
          this.columns.forEach((other, otherId) => {
            if (otherId === id) return;
            other.root.classList.add('is-collapsed');
            other.root.classList.remove('is-active', 'is-closing', 'is-shrinking');
          });
          this._updateVisibleCount();
          this.openColumn(id, { scroll: true, active: true });
        } else {
          this.toggleColumn(id, { scroll: false, active: true });
        }
      });
    }

    this.columns.set(id, {
      root: colEl,
      head,
      scroll,
      handle,
      close,
      section: colEl.dataset.section || null,
    });
  }

  _recomputeOrderFromDOM() {
    const ids = Array.from(this.deck.querySelectorAll('.col'))
      .map(el => String(el.dataset.id || ''))
      .filter(Boolean);
    this.order = ids.filter(id => this.columns.has(id));
  }

  _getColumnByKey(key) {
    if (key == null) return null;
    const k = String(key);

    // 1) direct id match
    if (this.columns.has(k)) return this.columns.get(k);

    // 2) match by data-section
    for (const c of this.columns.values()) {
      if (c.section === k) return c;
    }

    // 3) match by DOM just in case
    const el = this.deck.querySelector(`.col[data-id="${CSS.escape(k)}"], .col[data-section="${CSS.escape(k)}"]`);
    if (el) {
      this._adoptColumnElement(el);
      return this.columns.get(el.dataset.id);
    }

    return null;
  }

  _updateVisibleCount() {
    const visible = Array.from(this.columns.values())
      .filter(c => !c.root.classList.contains('is-collapsed') && !c.root.classList.contains('is-closing')).length;
    this.deck.dataset.visible = String(visible);
    document.body.style.setProperty('--visible', visible);
    return visible;
  }

  _syncReorderAvailability() {
    const visible = Number(this.deck.dataset.visible || '0');
    const can = visible > 1;
    this.deck.dataset.canReorder = can ? 'true' : 'false';

    this.columns.forEach(({ root, handle }) => {
      if (!handle) return;
      const isVisible = !root.classList.contains('is-collapsed');
      handle.style.cursor = can && isVisible ? 'grab' : 'default';
    });
  }

  _isMobile() {
    return window.matchMedia(`(max-width: ${this.mobileBP}px)`).matches;
  }

  /* =============================================================================
    Header / Nav: toggle-open and lazy-load missing columns
  ============================================================================= */

  _bindNav() {
    if (this._navBound) return;
    this._navBound = true;

    this.navMount.addEventListener('click', this._onNavActivate, true);
    this.navMount.addEventListener('keydown', this._onNavActivate, true);
  }

  async _onNavActivate(e) {
    const isKey = e.type === 'keydown';
    if (isKey && !(e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) return;

    const openEl = e.target.closest?.(`[${this.openAttr}]`);
    const toggleEl = e.target.closest?.(`[${this.toggleAttr}]`);
    const el = openEl || toggleEl;
    if (!el) return;

    e.preventDefault();

    const key = openEl ? el.getAttribute(this.openAttr) : el.getAttribute(this.toggleAttr);
    if (!key) return;

    // Lazy-load if missing
    if (!this._getColumnByKey(key)) {
      const ok = await this._ensureColumnLoaded(key, el);
      if (!ok) return;
    }

    if (openEl) this.openColumn(key, { scroll: true, active: true });
    else this.toggleColumn(key, { scroll: false, active: true });
  }

  _bindTabs() {
    if (!this.tabs || this._tabsBound) return;
    this._tabsBound = true;

    // Special-case About: toggle as a lazy-loaded column
    this.tabs.addEventListener('click', async (e) => {
      const tab = e.target?.closest?.('.tab[data-url][data-id]');
      if (!tab) return;

      const id = tab.getAttribute('data-id');
      const url = tab.getAttribute('data-url');

      if (id === 'about') {
        e.preventDefault();
        if (!this._getColumnByKey('about')) {
          const ok = await this._ensureColumnLoaded('about', tab);
          if (!ok) return;
        }
        this.toggleColumn('about', { scroll: false, active: true });
        return;
      }

      if (url) window.location.assign(url);
    });

    this.tabs.addEventListener('keydown', async (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const tab = e.target?.closest?.('.tab[data-url][data-id]');
      if (!tab) return;

      const id = tab.getAttribute('data-id');
      const url = tab.getAttribute('data-url');

      if (id === 'about') {
        e.preventDefault();
        if (!this._getColumnByKey('about')) {
          const ok = await this._ensureColumnLoaded('about', tab);
          if (!ok) return;
        }
        this.toggleColumn('about', { scroll: false, active: true });
        return;
      }

      if (url) {
        e.preventDefault();
        window.location.assign(url);
      }
    });
  }

  _syncNavButtons() {
    if (this._skipNavSync) return;
    // Hide header button when its column is open AND present.
    // If the column isn't present yet, keep the button visible (so it can fetch).
    const buttons = Array.from(this.navMount.querySelectorAll(`[${this.openAttr}], [${this.toggleAttr}]`));

    buttons.forEach((btn) => {
      const key = btn.getAttribute(this.openAttr) || btn.getAttribute(this.toggleAttr);
      if (!key) return;

      const c = this._getColumnByKey(key);
      if (!c) {
        btn.hidden = false;
        btn.setAttribute('aria-hidden', 'false');
        return;
      }

      const isOpen = !c.root.classList.contains('is-collapsed') && !c.root.classList.contains('is-closing');
      const wasHidden = btn.hidden;

      btn.hidden = isOpen;
      btn.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
      btn.setAttribute('aria-pressed', isOpen ? 'true' : 'false');

      // Trigger pop-in animation when button becomes visible
      if (wasHidden && !isOpen) {
        // Move to front so most-recently-closed appears on the left
        btn.parentNode.prepend(btn);
        btn.classList.remove('is-popping');
        // Force reflow to restart animation
        void btn.offsetWidth;
        btn.classList.add('is-popping');
        btn.addEventListener('animationend', () => {
          btn.classList.remove('is-popping');
        }, { once: true });
      }
    });
  }

  _inferColumnURL(key, sourceEl) {
    const explicit =
      sourceEl?.getAttribute?.('data-url') ||
      sourceEl?.getAttribute?.('data-fetch-url') ||
      sourceEl?.dataset?.url ||
      sourceEl?.dataset?.fetchUrl;

    if (explicit) return explicit;

    const k = String(key);
    if (k.startsWith('/')) return k;
    return `/${encodeURIComponent(k)}`;
  }

  async _ensureColumnLoaded(key, sourceEl) {
    if (this._getColumnByKey(key)) return true;

    const url = this._inferColumnURL(key, sourceEl);
    if (!url) return false;

    // Dedupe inflight per key
    if (this._inflightColumns.has(key)) {
      try { await this._inflightColumns.get(key); } catch (_) {}
      return !!this._getColumnByKey(key);
    }

    const p = (async () => {
      const res = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } });
      if (!res.ok) throw new Error(`ColumnDeck: failed to fetch ${url} (HTTP ${res.status})`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const k = String(key);
      const esc = CSS.escape(k);

      // Prefer a real column in response
      let col =
        doc.querySelector(`.col[data-id="${esc}"]`) ||
        doc.querySelector(`.col[data-section="${esc}"]`) ||
        doc.querySelector('.deck .col') ||
        doc.querySelector('.col');

      let imported;
      if (col) {
        imported = document.importNode(col, true);
      } else {
        // Build from a content root
        const contentRoot = doc.querySelector(this.contentSelector) || doc.querySelector('main') || doc.body;

        imported = document.createElement('section');
        imported.className = 'col';
        imported.dataset.id = k;
        imported.dataset.section = k;

        const head = document.createElement('header');
        head.className = 'head';

        const drag = document.createElement('button');
        drag.type = 'button';
        drag.className = 'drag-handle';
        drag.setAttribute('aria-label', 'Drag to reorder');
        drag.textContent = '⋮⋮';

        const h3 = document.createElement('h3');
        h3.textContent = (sourceEl?.textContent || '').trim() || k;

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'close';
        close.setAttribute('aria-label', 'Close column');
        close.textContent = '×';

        head.append(drag, h3, close);

        const scroll = document.createElement('div');
        scroll.className = 'scroll';

        // Clone children (strip nav/tabs)
        const temp = document.createElement('div');
        const cloned = document.importNode(contentRoot, true);
        temp.appendChild(cloned);

        this.stripSelectors.forEach(sel => {
          temp.querySelectorAll(sel).forEach(n => n.remove());
        });

        const root = temp.firstElementChild || temp;
        Array.from(root.childNodes).forEach(n => scroll.appendChild(n.cloneNode(true)));

        imported.append(head, scroll);
      }

      // Normalize ids
      imported.dataset.id = k;
      imported.dataset.section = imported.dataset.section || k;

      // Default state: collapsed until toggled/opened
      imported.classList.add('is-collapsed');

      this.deck.appendChild(imported);
      this._adoptColumnElement(imported);

      this._updateVisibleCount();
      this._syncReorderAvailability();
      this._syncNavButtons();
      return true;
    })();

    this._inflightColumns.set(key, p);
    try {
      return await p;
    } catch (err) {
      console.warn(err);
      return false;
    } finally {
      this._inflightColumns.delete(key);
    }
  }

  /* =============================================================================
    Drag reorder (pointer) — live placeholder gap, commit on drop
  ============================================================================= */

  _onPointerDown(e, id) {
    if (this._isMobile()) return;
    if (e.button != null && e.button !== 0) return;

    const visible = Number(this.deck.dataset.visible || '0');
    if (visible <= 1) return;

    const c = this.columns.get(id);
    if (!c) return;
    if (c.root.classList.contains('is-collapsed')) return;

    e.preventDefault();
    this._lockScroll();

    const col = c.root;
    const rect = col.getBoundingClientRect();

    this._drag.id = id;
    this._drag.col = col;
    this._drag.handle = e.currentTarget;
    this._drag.pointerId = e.pointerId;
    this._drag.offsetX = e.clientX - rect.left;
    this._drag.offsetY = e.clientY - rect.top;

    // Placeholder at original position
    const ph = document.createElement('section');
    ph.className = 'col col--placeholder';
    ph.style.width = `${rect.width}px`;
    ph.style.height = `${rect.height}px`;
    ph.style.flex = getComputedStyle(col).flex || '';
    this._drag.placeholder = ph;

    // Put placeholder where the column was, then move column to body so flex layout doesn't reflow.
    this.deck.insertBefore(ph, col);
    document.body.appendChild(col);

    col.classList.add('is-dragging');
    document.body.classList.add('col-moving');

    col.style.position = 'fixed';
    col.style.left = `${rect.left}px`;
    col.style.top = `${rect.top}px`;
    col.style.width = `${rect.width}px`;
    col.style.height = `${rect.height}px`;
    col.style.margin = '0';
    col.style.zIndex = '9999';
    col.style.pointerEvents = 'none';
    col.style.transition = 'none';
    col.style.transform = 'scale(1.03)';
    col.style.transformOrigin = 'center top';

    this.deck.classList.add('deck--dragging');

    try { this._drag.handle.setPointerCapture(e.pointerId); } catch (_) {}

    window.addEventListener('pointermove', this._onPointerMove, { passive: false });
    window.addEventListener('pointerup', this._onPointerUp, { passive: false });

    this._moveDraggedToPointer(e);

    // Start swing physics
    this._drag.prevClientX = e.clientX;
    this._drag.angle = 0;
    this._drag.angularVel = 0;
    this._startSwingLoop();

    this._liveReorder(e);
  }

  _onPointerMove(e) {
    if (!this._drag.id || e.pointerId !== this._drag.pointerId) return;
    e.preventDefault();
    this._moveDraggedToPointer(e);
    this._liveReorder(e);
  }

  _onPointerUp(e) {
    if (!this._drag.id || e.pointerId !== this._drag.pointerId) return;
    e.preventDefault();

    this._commitDrop();
    this._unlockScroll();

    window.removeEventListener('pointermove', this._onPointerMove, { passive: false });
    window.removeEventListener('pointerup', this._onPointerUp, { passive: false });

    try { this._drag.handle?.releasePointerCapture?.(this._drag.pointerId); } catch (_) {}

    this._drag.id = null;
    this._drag.col = null;
    this._drag.handle = null;
    this._drag.pointerId = null;
    this._drag.offsetX = 0;
    this._drag.offsetY = 0;
    this._drag.lastInsertKey = null;
  }

  _moveDraggedToPointer(e) {
    const col = this._drag.col;
    if (!col) return;
    col.style.left = `${e.clientX - this._drag.offsetX}px`;
    col.style.top = `${e.clientY - this._drag.offsetY}px`;

    // Smooth impulse — blend current delta with previous to remove jitter
    const dx = e.clientX - this._drag.prevClientX;
    this._drag.smoothDx = (this._drag.smoothDx || 0) * 0.6 + dx * 0.4;
    this._drag.angularVel += this._drag.smoothDx * 0.012;
    this._drag.prevClientX = e.clientX;
  }

  _startSwingLoop() {
    const stiffness = 0.07;  // strong pull back to center
    const damping = 0.86;    // settles quickly when idle
    const softLimit = 4;     // soft cap — keeps it subtle

    const loop = () => {
      if (!this._drag.id) return;

      // Spring: pull angle back toward 0
      this._drag.angularVel += -this._drag.angle * stiffness;

      // Soft limit — extra resistance past the cap (no hard clamp)
      if (Math.abs(this._drag.angle) > softLimit) {
        const over = this._drag.angle - Math.sign(this._drag.angle) * softLimit;
        this._drag.angularVel -= over * 0.15;
      }

      // Friction
      this._drag.angularVel *= damping;
      // Integrate
      this._drag.angle += this._drag.angularVel;

      const col = this._drag.col;
      if (col) {
        col.style.transform = `scale(1.03) rotate(${this._drag.angle.toFixed(2)}deg)`;
      }

      this._drag.swingRaf = requestAnimationFrame(loop);
    };
    this._drag.swingRaf = requestAnimationFrame(loop);
  }

  _stopSwingLoop() {
    cancelAnimationFrame(this._drag.swingRaf);
    this._drag.swingRaf = 0;
  }

  _animateSettle(col, angle, vel) {
    const stiffness = 0.025;
    const damping = 0.90;
    col.style.transformOrigin = 'center top';

    const tick = () => {
      vel += -angle * stiffness;
      vel *= damping;
      angle += vel;

      if (Math.abs(angle) < 0.05 && Math.abs(vel) < 0.05) {
        col.style.transform = '';
        col.style.transformOrigin = '';
        return;
      }

      col.style.transform = `rotate(${angle.toFixed(2)}deg)`;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  _liveReorder(e) {
    const ph = this._drag.placeholder;
    const dragged = this._drag.col;
    if (!ph || !dragged) return;

    const axis = this._isMobile() ? 'y' : 'x';

    // Use the center of the dragged column instead of pointer position
    const draggedRect = dragged.getBoundingClientRect();
    const draggedCenter = axis === 'y'
      ? (draggedRect.top + draggedRect.height / 2)
      : (draggedRect.left + draggedRect.width / 2);

    const cols = Array.from(this.deck.querySelectorAll('.col'))
      .filter(el => el !== ph && !el.classList.contains('is-collapsed'));

    let insertBefore = null;
    for (const el of cols) {
      const r = el.getBoundingClientRect();
      const mid = axis === 'y' ? (r.top + r.height / 2) : (r.left + r.width / 2);
      if (draggedCenter < mid) { insertBefore = el; break; }
    }

    const key = insertBefore ? `${insertBefore.dataset.id}:before` : 'end';
    if (key === this._drag.lastInsertKey) return;
    this._drag.lastInsertKey = key;

    // FLIP animate shifts
    const movers = cols.filter(el => el !== ph);
    const before = new Map();
    movers.forEach(el => before.set(el, el.getBoundingClientRect()));

    if (insertBefore) this.deck.insertBefore(ph, insertBefore);
    else this.deck.appendChild(ph);

    const after = new Map();
    movers.forEach(el => after.set(el, el.getBoundingClientRect()));

    this._flip(movers, before, after);
  }

  _flip(elements, before, after) {
    cancelAnimationFrame(this._drag.raf);

    elements.forEach(el => {
      const b = before.get(el);
      const a = after.get(el);
      if (!b || !a) return;
      const dx = b.left - a.left;
      const dy = b.top - a.top;
      if (!dx && !dy) return;

      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
    });

    this._drag.raf = requestAnimationFrame(() => {
      elements.forEach(el => {
        if (!el.style.transform) return;
        el.style.transition = 'transform 180ms ease';
        el.style.transform = '';
        const onEnd = () => {
          el.style.transition = '';
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      });
    });
  }

  _commitDrop() {
    const col = this._drag.col;
    const ph = this._drag.placeholder;
    if (!col || !ph) return;

    this._stopSwingLoop();
    const settleAngle = this._drag.angle;
    const settleVel = this._drag.angularVel;

    this.deck.insertBefore(col, ph);
    ph.remove();

    col.classList.remove('is-dragging');
    document.body.classList.remove('col-moving');
    col.style.position = '';
    col.style.left = '';
    col.style.top = '';
    col.style.width = '';
    col.style.height = '';
    col.style.margin = '';
    col.style.zIndex = '';
    col.style.pointerEvents = '';
    col.style.transition = '';

    // Snap clean — no settle animation
    col.style.transform = '';
    col.style.transformOrigin = '';

    this.deck.classList.remove('deck--dragging');

    this._drag.placeholder = null;

    this._recomputeOrderFromDOM();
    this._updateVisibleCount();
    this._syncReorderAvailability();
    this._syncNavButtons();
  }

  /* =============================================================================
    Scroll lock (during drag)
  ============================================================================= */

  _lockScroll() {
    if (this._drag.locked) return;
    this._drag.locked = true;

    const html = document.documentElement;
    const body = document.body;

    this._drag.prev.htmlOverflow = html.style.overflow;
    this._drag.prev.bodyOverflow = body.style.overflow;
    this._drag.prev.htmlOverscroll = html.style.overscrollBehavior;
    this._drag.prev.bodyOverscroll = body.style.overscrollBehavior;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';

    // Hide scrollbars inside columns while dragging
    this.columns.forEach(({ scroll }) => {
      if (!scroll) return;
      this._drag.prev.scrollOverflows.set(scroll, scroll.style.overflow);
      scroll.style.overflow = 'hidden';
    });

    const prevent = (ev) => ev.preventDefault();
    this._drag._preventScroll = prevent;
    window.addEventListener('wheel', prevent, { passive: false });
    window.addEventListener('touchmove', prevent, { passive: false });
  }

  _unlockScroll() {
    if (!this._drag.locked) return;
    this._drag.locked = false;

    const html = document.documentElement;
    const body = document.body;

    html.style.overflow = this._drag.prev.htmlOverflow || '';
    body.style.overflow = this._drag.prev.bodyOverflow || '';
    html.style.overscrollBehavior = this._drag.prev.htmlOverscroll || '';
    body.style.overscrollBehavior = this._drag.prev.bodyOverscroll || '';

    this._drag.prev.scrollOverflows.forEach((val, el) => { el.style.overflow = val || ''; });
    this._drag.prev.scrollOverflows.clear();

    if (this._drag._preventScroll) {
      window.removeEventListener('wheel', this._drag._preventScroll, { passive: false });
      window.removeEventListener('touchmove', this._drag._preventScroll, { passive: false });
      this._drag._preventScroll = null;
    }
  }
}
